import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { fetchMyTenants } from '@/lib/api';
import type { User, Session } from '@supabase/supabase-js';
import type { Tenant } from '@/types';

interface MyTenantEntry { tenant: Tenant; role: string }

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSuperAdmin: boolean;
  myTenants: MyTenantEntry[];
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [myTenants, setMyTenants] = useState<MyTenantEntry[]>([]);
  const redirectedForInvite = useRef(false);

  useEffect(() => {
    // ── Detect invite/recovery tokens in URL hash ─────────────────────
    // When a user clicks an invite link, Supabase redirects here with
    // tokens in the hash (e.g., #access_token=...&type=invite).
    // If Supabase didn't honour the redirect_to (e.g., URL not in allowed
    // list), the user lands on "/" instead of "/auth/reset-password".
    // We detect this and redirect them to the password setup page.
    const hash = window.location.hash;
    if (hash && !redirectedForInvite.current) {
      const params = new URLSearchParams(hash.substring(1));
      const type = params.get('type');
      if ((type === 'invite' || type === 'recovery') && location.pathname !== '/auth/reset-password') {
        redirectedForInvite.current = true;
        // Let Supabase JS process the tokens first, then redirect
        setTimeout(() => navigate('/auth/reset-password', { replace: true }), 100);
      }
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) loadUserRole(s.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_ev, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        loadUserRole(s.user.id);
      } else {
        setIsSuperAdmin(false);
        setMyTenants([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadUserRole(uid: string) {
    // Ensure loading=true while role data is being fetched —
    // prevents flash of "No Access" when onAuthStateChange fires
    // after login (loading was already false from initial getSession).
    setLoading(true);

    // Check both in parallel
    const [adminRes, tenantRes] = await Promise.all([
      supabase.from('platform_admins').select('role').eq('user_id', uid).maybeSingle(),
      fetchMyTenants().catch(() => []),
    ]);
    setIsSuperAdmin(adminRes.data?.role === 'super_admin' || adminRes.data?.role === 'admin');
    setMyTenants(tenantRes as MyTenantEntry[]);
    setLoading(false);
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };
  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };
  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isSuperAdmin, myTenants, signIn, signUp, signOut, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
